import { useState, useEffect } from "react";
import { Footprints, FileText, Camera, Search, Lightbulb } from "lucide-react";
import { sound } from "@/lib/sound";

const CLUES = [
  { id: "foot", Icon: Footprints, x: 18, y: 62, label: "Footprints" },
  { id: "note", Icon: FileText, x: 72, y: 34, label: "Torn note" },
  { id: "photo", Icon: Camera, x: 54, y: 80, label: "Photograph" },
];
const LIMIT = 30;

export default function ClueHunt({ world, onWin }) {
  const [found, setFound] = useState({});
  const [time, setTime] = useState(LIMIT);
  const count = Object.keys(found).length;

  useEffect(() => {
    if (count >= 3) return;
    const id = setInterval(() => {
      setTime((t) => {
        if (t <= 1) {
          sound.playError();
          setFound({});
          return LIMIT;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [count]);

  const hit = (id) => {
    if (found[id]) return;
    const nf = { ...found, [id]: true };
    setFound(nf);
    sound.playClick();
    if (Object.keys(nf).length >= 3) {
      sound.playGift();
      onWin();
    }
  };

  return (
    <div className="text-center">
      <p className="text-sm text-white/70">Find all 3 hidden clues before time runs out.</p>
      <div
        className="relative mx-auto mt-5 h-56 w-full overflow-hidden rounded-2xl border"
        style={{
          borderColor: `${world.accent}40`,
          background: "linear-gradient(160deg, rgba(40,0,12,0.6), rgba(0,0,0,0.88))",
          cursor: "crosshair",
        }}
        data-testid="clue-scene"
      >
        <Lightbulb className="absolute right-6 top-5 h-8 w-8 text-white/10" />
        <div className="absolute bottom-0 left-0 h-10 w-full bg-black/40" />
        {CLUES.map((c) => {
          const I = c.Icon;
          const got = found[c.id];
          return (
            <button
              key={c.id}
              onClick={() => hit(c.id)}
              data-testid={`clue-${c.id}`}
              className="absolute flex items-center justify-center rounded-full transition-all hover:brightness-150"
              style={{
                left: `${c.x}%`,
                top: `${c.y}%`,
                transform: "translate(-50%, -50%)",
                width: got ? 44 : 34,
                height: got ? 44 : 34,
                background: got ? world.accent : "rgba(255,255,255,0.06)",
                border: `1px solid ${got ? world.accent : "rgba(255,255,255,0.15)"}`,
                boxShadow: got ? `0 0 18px ${world.accentSoft}` : "none",
              }}
            >
              {got && <I className="h-5 w-5 text-black" />}
            </button>
          );
        })}
        <div className="absolute left-3 top-3 flex items-center gap-1 rounded-full bg-black/50 px-2 py-1 text-xs text-white/70">
          <Search className="h-3 w-3" /> {count}/3
        </div>
        <div className="absolute right-3 top-3 rounded-full bg-black/50 px-2 py-1 text-xs text-white/70">{time}s</div>
      </div>
      <div className="mt-3 flex justify-center gap-2">
        {CLUES.map((c) => (
          <span
            key={c.id}
            className="rounded-full border px-2 py-1 text-[0.65rem]"
            style={{
              borderColor: found[c.id] ? world.accent : "rgba(255,255,255,0.15)",
              color: found[c.id] ? world.accent : "rgba(255,255,255,0.4)",
            }}
          >
            {c.label}
          </span>
        ))}
      </div>
    </div>
  );
}
